import { useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCartOutlined'
import TableRowsIcon from '@mui/icons-material/TableRows'
import GridViewIcon from '@mui/icons-material/GridView'
import InventoryIcon from '@mui/icons-material/Inventory2'
import {
  computeDocument,
  format,
  money,
  prixTTC,
  referenceAffichable,
  type CurrencyCode,
  type Customer,
  type Item,
  type Transaction,
} from '@iobiz/shared'
import {
  useCustomers,
  useItems,
  useSessionCaisse,
  useStockLevels,
  useSyncStatus,
} from '../data/hooks.js'
import { useSessionStore } from '../stores/sessionStore.js'
import {
  creerDocument,
  encaisserVente,
  type PaiementSaisi,
} from '../data/repositories/sales.write.js'
import { EncaissementDialog } from '../features/sales/EncaissementDialog.js'
import { TicketCaisse } from '../features/sales/TicketCaisse.js'
import {
  mesurerPageTicket,
  PortailImpression,
  ReglesImpression,
} from '../features/sales/impression.js'

/**
 * Mode caisse plein écran (W-15).
 *
 * ⭐ L'écran le plus utilisé du produit selon le plan de livraison, et le seul
 * qu'on tient d'une main devant un client qui attend. Tout y est dimensionné
 * pour le pouce et le coup d'œil : cibles larges, montant dû en grand, aucune
 * navigation qui fasse perdre le panier.
 *
 * ⚠️ Il vit HORS de la coquille applicative : ni barre latérale, ni fil
 * d'Ariane. Un caissier qui clique par mégarde sur « Rapports » en pleine
 * vente perdrait son panier — la sortie est un geste unique et explicite.
 *
 * ⭐ LE MÊME ÉCRAN sert à rédiger un devis.
 *
 * Composer un panier est le même geste, qu'on encaisse ou qu'on chiffre : même
 * recherche, même douchette, mêmes vues liste et grille, mêmes totaux calculés
 * par `computeDocument`. Un second écran aurait divergé au premier ajout — et
 * c'est déjà arrivé : le devis avait été livré sous forme de tableau de saisie,
 * sans scan ni catalogue. Seules changent la fin de course (encaisser ou
 * enregistrer) et l'obligation d'un client.
 */

interface LignePanier {
  itemId: string
  label: string
  unitPrice: number
  taxRate: number
  quantity: number
}

export function CaissePage({
  mode = 'vente',
}: { mode?: 'vente' | 'devis' | 'commande' } = {}): JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  /** Devis et commande partagent tout : un document engageant, sans encaissement. */
  const engagement = mode !== 'vente'
  const devis = mode === 'devis'
  const titre = devis ? 'Nouveau devis' : mode === 'commande' ? 'Nouvelle commande' : 'Caisse'
  const store = useSessionStore((s) => s.stores.find((b) => b.slug === slug))
  const storeId = store?.storeId
  const devise = (store?.currencyCode ?? 'XAF') as CurrencyCode
  const sync = useSyncStatus()
  const utilisateur = useSessionStore((s) => s.user)
  const caissier = utilisateur
    ? `${utilisateur.firstName} ${utilisateur.lastName}`.trim() || utilisateur.email
    : undefined

  // ⚠️ Déclaré AVANT la lecture du catalogue : la limite de la liste des
  // clients en dépend, et une constante utilisée avant sa déclaration ne
  // survit pas au premier rendu.
  const [parametres] = useSearchParams()
  const [recherche, setRecherche] = useState('')
  const [panier, setPanier] = useState<LignePanier[]>([])
  const [client, setClient] = useState<Customer | null>(null)
  /**
   * Vue du catalogue, comme au catalogue lui-même.
   *
   * ⭐ La grille sert au repérage visuel — c'est la vue du comptoir. La liste
   * sert quand les articles se ressemblent et qu'on compare des prix : vingt
   * pavés carrés obligent alors à balayer la page en zigzag.
   */
  const [vue, setVue] = useState<'grille' | 'liste'>('grille')
  const [remise, setRemise] = useState('0')
  const [encaissement, setEncaissement] = useState(false)
  /**
   * La dernière vente encaissée, telle qu'elle part à l'imprimante.
   *
   * ⚠️ Cet état SURVIT à l'avis : le document doit rester monté pendant que la
   * fenêtre d'impression est ouverte, et l'avis, lui, s'efface tout seul au
   * bout de huit secondes. Les lier aurait démonté le ticket sous le nez du
   * caissier resté sur la boîte de dialogue.
   */
  const [ticket, setTicket] = useState<{ vente: Transaction; rendu: number } | null>(null)
  const [avis, setAvis] = useState(false)
  /** Format de page du ticket, mesuré juste avant d'imprimer. */
  const [pageTicket, setPageTicket] = useState<string | null>(null)
  const champRecherche = useRef<HTMLInputElement>(null)

  /**
   * ⭐ La caisse ouverte, LUE DANS LA RÉPLIQUE. Le verrou « pas de vente sans
   * caisse » s'appuie dessus à chaque encaissement : le demander au réseau
   * ferait échouer le verrou à la première coupure, c'est-à-dire qu'il
   * bloquerait la vente précisément là où le produit promet de tenir.
   *
   * ⚠️ `undefined` = réplique pas encore lue, `null` = aucune caisse ouverte.
   * Les confondre refuserait la vente le temps d'un battement au chargement.
   */
  const sessionCaisse = useSessionCaisse(storeId)

  const catalogue = useItems(storeId, { limit: 60, status: 'active', q: recherche || undefined })
  // ⚠️ Assez large pour retrouver le client visé par `?client=` : la liste sert
  // aussi à préremplir, et vingt fiches n'y suffisaient pas. Vaut pour la VENTE
  // autant que pour le devis — on part de la fiche client dans les deux cas.
  const clients = useCustomers(storeId, { limit: parametres.get('client') ? 500 : 20 })
  const stocks = useStockLevels(storeId)

  const prix = (v: number): string => format(money(v, devise), 'fr-FR')
  const horsLigne = sync.phase === 'offline' || sync.phase === 'error'
  /** Nombre d'ARTICLES, pas de lignes : trois exemplaires font trois articles. */
  const nombreArticles = panier.reduce((somme, l) => somme + l.quantity, 0)

  /**
   * Reprise d'un DEVIS.
   *
   * ⭐ « Transformer en vente » depuis un devis ouvre cette page avec ses
   * lignes déjà là. Le devis n'est pas converti d'autorité : le commerçant
   * ajuste — une quantité change, un article n'est plus en stock — puis
   * encaisse. Le devis, lui, reste le document qu'il était.
   */
  const emplacement = useLocation()
  const etat = emplacement.state as {
    lignes?: LignePanier[]
    customerId?: string
    origineId?: string
  } | null
  // ⚠️ Retenu à part : l'état de navigation est effacé juste après, et le
  // rattachement n'a lieu qu'à l'encaissement, parfois bien plus tard.
  const [origineId, setOrigineId] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (!etat?.lignes?.length) return
    setPanier(etat.lignes)
    setOrigineId(etat.origineId)
    /**
     * ⚠️ On efface l'état de navigation PAR LE ROUTEUR, jamais par
     * `window.history.replaceState` : celui-ci écraserait aussi la clé que
     * React Router range dans l'historique, et la navigation arrière se mettrait
     * à sauter des étapes. Sans cet effacement, revenir en arrière puis en avant
     * re-remplirait le panier par-dessus la vente en cours.
     */
    navigate(emplacement.pathname, { replace: true, state: null })
  }, [etat, emplacement.pathname, navigate])

  /**
   * Client préselectionné : par l'état de navigation (devis transformé en
   * vente) ou par l'URL (« Nouveau devis » depuis une fiche client).
   *
   * ⚠️ Par l'URL et non par l'état de navigation pour le devis : l'adresse
   * reste alors rechargeable et partageable, comme le reste de l'application.
   */
  const clientVise = etat?.customerId ?? parametres.get('client') ?? undefined
  useEffect(() => {
    if (!clientVise || client) return
    const trouve = (clients?.data ?? []).find((c) => c.id === clientVise)
    if (trouve) setClient(trouve)
  }, [clientVise, clients, client])

  const totaux = useMemo(
    () =>
      computeDocument({
        lines: panier.map((l) => ({
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          taxRate: l.taxRate,
        })),
        currency: devise,
        globalDiscountRate: Number(remise) || 0,
      }),
    [panier, devise, remise],
  )

  function ajouter(item: Item): void {
    setPanier((p) => {
      const existante = p.find((l) => l.itemId === item.id)
      if (existante) {
        return p.map((l) => (l.itemId === item.id ? { ...l, quantity: l.quantity + 1 } : l))
      }
      return [
        ...p,
        {
          itemId: item.id,
          // ⚠️ Le libellé est COPIÉ maintenant : renommer l'article demain ne
          // doit pas réécrire le ticket d'aujourd'hui.
          label: item.name,
          unitPrice: item.salePrice,
          // ⚠️ Le taux vient de l'ARTICLE, résolu dans la réplique. Il était
          // figé à zéro : chaque ticket sortait sans TVA.
          taxRate: item.taxRate ?? 0,
          quantity: 1,
        },
      ]
    })
  }

  function changerQuantite(itemId: string, delta: number): void {
    setPanier((p) =>
      p
        .map((l) => (l.itemId === itemId ? { ...l, quantity: l.quantity + delta } : l))
        .filter((l) => l.quantity > 0),
    )
  }

  /**
   * Entrée dans le champ de recherche.
   *
   * ⭐ Une douchette tape le code puis envoie « Entrée ». Si la recherche ne
   * ramène qu'un article, on l'ajoute et on vide le champ : le scan suivant
   * doit pouvoir enchaîner sans un seul clic.
   */
  function surEntree(): void {
    const trouves = catalogue?.data ?? []
    if (trouves.length === 1 && trouves[0]) {
      ajouter(trouves[0])
      setRecherche('')
      champRecherche.current?.focus()
    }
  }

  /**
   * Fin de course d'un ENGAGEMENT — devis ou commande.
   *
   * ⚠️ Ni l'un ni l'autre ne sort de stock ni n'entre dans le chiffre
   * d'affaires : ce sont des engagements, pas des recettes.
   */
  async function enregistrerEngagement(): Promise<void> {
    if (!storeId || !client) return
    const document = await creerDocument(storeId, devis ? 'quote' : 'order', {
      lines: panier.map((l) => ({
        itemId: l.itemId,
        label: l.label,
        // ⚠️ zod 4 : un champ `.optional()` reste une clé du type inféré.
        description: undefined,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        discountRate: 0,
        taxRate: l.taxRate,
      })),
      customerId: client.id,
      customerName: client.displayName,
      globalDiscountRate: Number(remise) || 0,
      currencyCode: devise,
    })
    navigate(`/s/${slug}/ventes/${document.id}`)
  }

  async function valider(paiements: PaiementSaisi[]): Promise<void> {
    if (!storeId) return
    const vente = await encaisserVente(
      storeId,
      {
        lines: panier.map((l) => ({
          itemId: l.itemId,
          label: l.label,
          description: undefined,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          discountRate: 0,
          taxRate: l.taxRate,
        })),
        customerId: client?.id,
        customerName: client?.displayName,
        cashierName: caissier,
        // ⚠️ La vente est rattachée à la SESSION : sans ce lien, le
        // rapprochement de clôture ne verrait aucun encaissement et tout le
        // contenu du tiroir passerait pour un excédent inexpliqué.
        sessionId: sessionCaisse?.id,
        parentId: origineId,
        globalDiscountRate: Number(remise) || 0,
        currencyCode: devise,
      },
      paiements,
    )

    const encaisse = paiements.reduce((s, p) => s + p.amount, 0)
    setEncaissement(false)
    setAvis(true)

    /**
     * ⭐ Le ticket part à l'imprimante SANS qu'on le demande. C'est le geste du
     * comptoir : on encaisse, on tend le ticket. Le réclamer d'un second clic
     * ajoutait une manipulation à chaque vente de la journée, et un ticket
     * oublié se rattrape mal — le client est déjà parti.
     *
     * ⚠️ Un navigateur ne sait pas imprimer en silence : `window.print()`
     * ouvre la boîte de dialogue, et aucune page web ne peut s'en passer.
     * « Automatique » veut donc dire que la boîte s'ouvre seule, sur le bon
     * document et au bon format ; le caissier n'a plus qu'à valider.
     *
     * ⚠️ `flushSync` deux fois, et dans cet ordre : le ticket doit être rendu
     * pour être mesuré, et la hauteur de page connue avant l'impression.
     * `window.print()` fige la page telle qu'elle est à l'instant de l'appel —
     * sans rendu synchrone, il sortirait la vente PRÉCÉDENTE, ou rien.
     */
    flushSync(() => {
      setTicket({ vente, rendu: Math.max(0, encaisse - vente.totalAmount) })
      setPageTicket(null)
    })

    const lancer = (): void => {
      flushSync(() => setPageTicket(mesurerPageTicket()))
      window.print()
    }
    /**
     * ⚠️ On VÉRIFIE que le ticket est dans la page avant d'imprimer. React peut
     * refuser un rendu synchrone selon le contexte d'appel — ici on sort d'une
     * boîte de confirmation, elle-même dans une promesse — et le refus est
     * silencieux : `flushSync` se rabat sur une mise à jour ordinaire. On
     * mesurerait alors un bloc absent et on imprimerait une page vide, sans que
     * rien ne le signale. Une trame d'attente suffit à le laisser arriver.
     */
    if (document.getElementById('ticket')) lancer()
    else requestAnimationFrame(lancer)

    setPanier([])
    setClient(null)
    setRemise('0')
    champRecherche.current?.focus()
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* ---------------------------------------------------------- entête */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          px: 4,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Button
          startIcon={<CloseIcon />}
          onClick={() =>
            navigate(
              engagement && client ? `/s/${slug}/clients/${client.id}` : `/s/${slug}/ventes`,
            )
          }
          color="inherit"
        >
          {engagement ? `Quitter le ${devis ? 'devis' : 'la commande'}` : 'Quitter la caisse'}
        </Button>
        {engagement ? <DescriptionIcon color="primary" /> : <PointOfSaleIcon color="primary" />}
        <Typography variant="h3" sx={{ fontWeight: 700 }}>
          {titre}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {horsLigne && <Chip label="Hors ligne" color="warning" size="small" />}
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {store?.name}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 25rem' }, minHeight: 0 }}>
        {/* ------------------------------------------------------ catalogue */}
        <Box sx={{ p: 4, overflowY: 'auto', minHeight: 0 }}>
          <TextField
            inputRef={champRecherche}
            autoFocus
            fullWidth
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && surEntree()}
            placeholder="Scanner un code-barres ou rechercher un article…"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 4 }}
          />

          {/* ⭐ Grille ou liste, comme au catalogue.
              Un caissier qui connaît son rayon cherche une photo ; un autre
              compare des prix sur vingt références et veut des lignes. Le
              choix vaut pour la session — il ne se re-demande pas à chaque
              vente. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {(catalogue?.data ?? []).length} article
              {(catalogue?.data ?? []).length > 1 ? 's' : ''}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={vue}
              onChange={(_, v: 'grille' | 'liste' | null) => v && setVue(v)}
            >
              <ToggleButton value="liste" aria-label="Vue liste">
                <TableRowsIcon sx={{ fontSize: 18 }} />
              </ToggleButton>
              <ToggleButton value="grille" aria-label="Vue grille">
                <GridViewIcon sx={{ fontSize: 18 }} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {vue === 'grille' ? (
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: 'repeat(auto-fill, minmax(9.5rem, 1fr))',
              }}
            >
              {(catalogue?.data ?? []).map((item) => {
                const quantite = stocks.get(item.id) ?? 0
                const rupture = item.trackStock && quantite <= 0
                return (
                  <Paper
                    key={item.id}
                    onClick={() => ajouter(item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && ajouter(item)}
                    sx={{
                      borderRadius: 3,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      border: 1,
                      borderColor: 'divider',
                      transition: 'none',
                      '&:hover': { borderColor: 'primary.main' },
                      '&:focus-visible': { outline: 2, outlineColor: 'primary.main' },
                    }}
                  >
                    {/* La photo OCCUPE la vignette : au comptoir, on reconnaît
                        un article à son image avant de lire son nom. */}
                    <Box
                      sx={{
                        height: 86,
                        bgcolor: 'action.hover',
                        display: 'grid',
                        placeItems: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      {item.imageUrl ? (
                        <Box
                          component="img"
                          src={item.imageUrl}
                          alt=""
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <InventoryIcon sx={{ fontSize: 28, color: 'text.disabled' }} />
                      )}
                    </Box>

                    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {item.name}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      {/* Le prix TTC, celui qu'on annonce au client. */}
                      <Typography className="tabular" sx={{ fontWeight: 700 }}>
                        {prix(prixTTC(item.salePrice, item.taxRate))}
                      </Typography>
                      {item.taxRate > 0 && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          dont TVA {item.taxRate} %
                        </Typography>
                      )}
                      {item.trackStock ? (
                        <Typography
                          variant="caption"
                          sx={{ color: rupture ? 'error.main' : 'text.secondary' }}
                        >
                          {rupture ? 'Rupture' : `${quantite} en stock`}
                        </Typography>
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Service
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                )
              })}
            </Box>
          ) : (
            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
              {(catalogue?.data ?? []).map((item) => {
                const quantite = stocks.get(item.id) ?? 0
                const rupture = item.trackStock && quantite <= 0
                return (
                  <Box
                    key={item.id}
                    onClick={() => ajouter(item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && ajouter(item)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                      px: 3,
                      // ⚠️ Cible haute : on touche cette ligne du pouce, avec
                      // un client qui attend. 56 px est le minimum tenable.
                      minHeight: 56,
                      borderBottom: 1,
                      borderColor: 'divider',
                      cursor: 'pointer',
                      '&:last-of-type': { borderBottom: 0 },
                      '&:hover': { bgcolor: 'action.hover' },
                      '&:focus-visible': { outline: 2, outlineColor: 'primary.main' },
                    }}
                  >
                    <Avatar
                      src={item.imageUrl ?? undefined}
                      variant="rounded"
                      sx={{ width: 36, height: 36, fontSize: 12, bgcolor: 'action.hover' }}
                    >
                      {item.name.slice(0, 2).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {item.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: rupture ? 'error.main' : 'text.secondary' }}
                      >
                        {!item.trackStock
                          ? 'Service'
                          : rupture
                            ? 'Rupture'
                            : `${quantite} en stock`}
                        {item.taxRate > 0 ? ` · TVA ${item.taxRate} %` : ''}
                      </Typography>
                    </Box>
                    <Typography className="tabular" sx={{ fontWeight: 700 }}>
                      {prix(prixTTC(item.salePrice, item.taxRate))}
                    </Typography>
                    <AddIcon sx={{ color: 'text.disabled' }} />
                  </Box>
                )
              })}
            </Paper>
          )}

          {(catalogue?.data ?? []).length === 0 && (
            <Typography sx={{ color: 'text.secondary', mt: 6, textAlign: 'center' }}>
              Aucun article ne correspond.
            </Typography>
          )}
        </Box>

        {/* --------------------------------------------------------- panier */}
        <Box
          sx={{
            borderLeft: { md: 1 },
            borderTop: { xs: 1, md: 0 },
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {/* ⭐ Le panier s'annonce comme un PANIER.
              C'était une colonne de champs sans titre : rien ne disait que
              cette moitié de l'écran contenait la vente en cours, et le nombre
              d'articles ne se lisait qu'en comptant les lignes. L'icône et le
              compteur donnent l'état en un coup d'œil, de loin, sans lire. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              px: 4,
              pt: 4,
              pb: 2,
            }}
          >
            <Badge badgeContent={nombreArticles} color="primary" showZero={false}>
              <ShoppingCartIcon sx={{ fontSize: 26 }} />
            </Badge>
            <Typography variant="h3" sx={{ fontWeight: 700 }}>
              Panier
            </Typography>
            <Box sx={{ flex: 1 }} />
            {panier.length > 0 && (
              <Tooltip title="Vider le panier">
                <IconButton
                  size="small"
                  aria-label="Vider le panier"
                  onClick={() => setPanier([])}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ px: 4, pb: 2 }}>
            <Autocomplete
              options={clients?.data ?? []}
              value={client}
              onChange={(_, v) => setClient(v)}
              getOptionLabel={(c) => c.displayName}
              renderInput={(params) => (
                <TextField
                  {...params}
                  // ⚠️ OBLIGATOIRE pour un devis : un chiffrage s'adresse à
                  // quelqu'un, et on le retrouve par sa fiche. Une vente
                  // comptoir, elle, est souvent anonyme.
                  label={engagement ? 'Client' : 'Client (facultatif)'}
                  required={engagement}
                  size="small"
                />
              )}
              renderOption={(props, c) => (
                <Box component="li" {...props} key={c.id} sx={{ display: 'flex', gap: 2 }}>
                  <Avatar sx={{ width: 26, height: 26, fontSize: '.7rem' }}>
                    {c.displayName.slice(0, 1)}
                  </Avatar>
                  {c.displayName}
                </Box>
              )}
            />
          </Box>

          <Divider />

          <Box sx={{ flex: 1, overflowY: 'auto', px: 4, py: 2, minHeight: 0 }}>
            {panier.length === 0 ? (
              <Box sx={{ textAlign: 'center', mt: 8, color: 'text.disabled' }}>
                <ShoppingCartIcon sx={{ fontSize: 48 }} />
                <Typography sx={{ color: 'text.secondary', mt: 2 }}>
                  Panier vide — scannez ou touchez un article.
                </Typography>
              </Box>
            ) : (
              panier.map((ligne, index) => (
                <Box key={ligne.itemId} sx={{ py: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {ligne.label}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label={`Retirer ${ligne.label}`}
                      onClick={() => setPanier((p) => p.filter((l) => l.itemId !== ligne.itemId))}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <IconButton
                      size="small"
                      aria-label="Diminuer"
                      onClick={() => changerQuantite(ligne.itemId, -1)}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography className="tabular" sx={{ minWidth: '2rem', textAlign: 'center' }}>
                      {ligne.quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Augmenter"
                      onClick={() => changerQuantite(ligne.itemId, 1)}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                    <Box sx={{ flex: 1 }} />
                    <Box sx={{ textAlign: 'right' }}>
                      {/* ⚠️ Le montant de la ligne vient de `computeDocument`,
                          pas d'une multiplication faite ici : c'est le même
                          calcul que le serveur rejouera, arrondis compris. Un
                          produit à la main aurait fini par ne plus totaliser
                          le TTC affiché en bas. */}
                      <Typography className="tabular" sx={{ fontWeight: 700 }}>
                        {prix(totaux.lines[index]?.total.amount ?? 0)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {prix(ligne.unitPrice)} HT
                        {ligne.taxRate > 0 ? ` · TVA ${ligne.taxRate} %` : ''}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Box>

          <Divider />

          <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Total HT
              </Typography>
              <Typography variant="body2" className="tabular">
                {prix(totaux.subtotalAmount.amount)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Remise
              </Typography>
              <TextField
                size="small"
                value={remise}
                onChange={(e) => setRemise(e.target.value.replace(/[^\d.]/g, ''))}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ width: '6.5rem' }}
                inputProps={{ inputMode: 'decimal', 'aria-label': 'Remise en pourcentage' }}
              />
            </Box>

            {/* ⚠️ La ligne TVA s'affiche MÊME à zéro. Sans elle, un panier
                d'articles sans taxe ne montrait aucune TVA, et le caissier ne
                pouvait pas distinguer « pas de taxe » d'un défaut d'affichage. */}
            {totaux.taxBreakdown.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  TVA
                </Typography>
                <Typography variant="body2" className="tabular" sx={{ color: 'text.secondary' }}>
                  {prix(0)}
                </Typography>
              </Box>
            ) : (
              totaux.taxBreakdown.map((t) => (
                <Box key={t.rate} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    TVA {t.rate} %
                  </Typography>
                  <Typography variant="body2" className="tabular">
                    {prix(t.amount.amount)}
                  </Typography>
                </Box>
              ))
            )}

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography sx={{ fontWeight: 700 }}>TOTAL TTC</Typography>
              <Typography variant="h2" className="tabular" sx={{ fontWeight: 800 }}>
                {prix(totaux.totalAmount.amount)}
              </Typography>
            </Box>

            <Button
              variant="contained"
              size="large"
              // ⚠️ Un devis SANS CLIENT n'a pas de destinataire : le bouton
              // reste inerte, et le champ est marqué obligatoire au-dessus.
              disabled={
                panier.length === 0 ||
                (engagement && !client) ||
                // ⚠️ Le verrou ne porte QUE sur l'encaissement : un devis ou
                // une commande n'ouvrent pas le tiroir, et les refuser aurait
                // privé la boutique de son travail de préparation hors des
                // heures de caisse.
                (!engagement && !sessionCaisse)
              }
              onClick={() => (engagement ? void enregistrerEngagement() : setEncaissement(true))}
              sx={{ py: 2, fontSize: '1.05rem' }}
            >
              {devis ? 'Créer le devis' : mode === 'commande' ? 'Créer la commande' : 'Encaisser'}
            </Button>
            {/* ⚠️ Un bouton inerte sans explication est un bug aux yeux du
                caissier : il clique, rien ne se passe, il recommence. On dit
                ce qui manque ET on donne le geste qui débloque.

                ⚠️ Le verrou est posé ICI, dans l'écran, et non sur le serveur.
                Une vente encaissée hors ligne remonte parfois APRÈS la
                clôture : la refuser côté serveur ferait perdre une vente bien
                réelle, encaissée alors que la caisse était ouverte. */}
            {!engagement && sessionCaisse === null && (
              <Alert
                severity="warning"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => navigate(`/s/${slug}/tiroir`)}
                  >
                    Ouvrir
                  </Button>
                }
              >
                Caisse fermée : aucun encaissement possible tant qu’elle n’est pas ouverte.
              </Alert>
            )}
            {engagement && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {devis
                  ? 'Un devis n’engage rien : aucun mouvement de stock, aucun chiffre d’affaires. Quand le client accepte, ouvrez-le et choisissez « Transformer en vente ».'
                  : 'Une commande n’est pas encore une vente : aucun mouvement de stock, aucun chiffre d’affaires tant qu’elle n’est pas facturée.'}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* La boîte d'encaissement n'a pas lieu d'être pour un devis : rien
          n'est réglé, et le document reste un brouillon. */}
      {!engagement && (
        <EncaissementDialog
          open={encaissement}
          du={totaux.totalAmount.amount}
          devise={devise}
          horsLigne={horsLigne}
          onAnnuler={() => setEncaissement(false)}
          onValider={(p) => void valider(p)}
        />
      )}

      {/* ⭐ Le ticket de la dernière vente, hors écran, prêt à partir. Il reste
          monté après l'impression : le caissier peut relancer la fenêtre par
          Ctrl+P si le premier tirage s'est mal passé, sans rouvrir la vente. */}
      <ReglesImpression format="ticket" pageTicket={pageTicket} />
      {ticket && (
        <PortailImpression>
          <TicketCaisse vente={ticket.vente} boutique={store} rendu={ticket.rendu} />
        </PortailImpression>
      )}

      <Snackbar
        open={avis}
        autoHideDuration={8000}
        onClose={() => setAvis(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setAvis(false)} sx={{ minWidth: '22rem' }}>
          {/* La référence et le rendu restent lisibles quelques secondes : ce
              sont les deux informations que le caissier annonce au client. */}
          Vente {ticket ? referenceAffichable(ticket.vente) : ''} enregistrée
          {ticket && ticket.rendu > 0 ? ` — rendu ${prix(ticket.rendu)}` : ''}
        </Alert>
      </Snackbar>
    </Box>
  )
}
